import { useMemo, useCallback } from 'react';
import { usePrompt } from '../contexts/PromptContext';
import { sortItemsBy } from '../../../helpers/listsHelpers';
import type { Prompt } from '../models/PromptTypes';

export interface PromptUIOption extends Prompt {
    label: string;
    searchableText: string;
}

export default function usePromptsInfo() {
    const { prompts, getPrompt, getAllPrompts } = usePrompt();

    const promptOptions: PromptUIOption[] = useMemo(() => {
        if (!prompts || prompts.length === 0) return [];
        return sortItemsBy(prompts, 'title').map((prompt) => ({
            ...prompt,
            label: prompt.title,
            searchableText: [
                prompt.title,
                prompt.description,
                prompt.category,
                prompt.assistant_key,
                prompt.tags,
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase(),
        }));
    }, [prompts]);

    const getPromptByKey = useCallback(
        (key: string) => promptOptions.find((p) => p.key === key),
        [promptOptions]
    );

    const getPromptsByCategory = useCallback(
        (category: string) => promptOptions.filter((p) => p.category === category),
        [promptOptions]
    );

    const getPromptsByAssistantKey = useCallback(
        (assistantKey: string) => promptOptions.filter((p) => p.assistant_key === assistantKey),
        [promptOptions]
    );

    const getPromptLabelByKey = useCallback(
        (key: string) => getPromptByKey(key)?.title ?? key,
        [getPromptByKey]
    );

    const getPublicPrompts = useMemo(() => {
        return promptOptions.filter((p) => p.is_public);
    }, [promptOptions]);

    return {
        promptOptions,
        getPromptByKey,
        getPromptsByCategory,
        getPromptsByAssistantKey,
        getPromptLabelByKey,
        getPublicPrompts,
        getPrompt,
        getAllPrompts,
    };
}